import { deepCopyObject } from 'utils/collectionUtil';
import { Features, hasFeature } from '@ardoq/features';
import {
  APISurveyAttributes,
  ArdoqId,
  MetaModel,
  SurveyApprovalRelationship,
  SurveyQuestionType,
  SurveyQuestionValidator,
  SurveySectionStatus,
  MetaModelTriple,
  SurveyFilterSetupAttributes,
  PersistedFieldSubQuestion,
  PersistedReferenceSubQuestion,
  isPersistedSurvey,
  UnpersistedReferenceSubQuestion,
  UnpersistedFieldSubQuestion,
  PersistedReferenceQuestion,
  UnpersistedReferenceQuestion,
  PersistedFieldQuestion,
  UnpersistedFieldQuestion,
  SurveyApprovalFallbackAudience,
  SurveyApprovalFlow,
  APISurveyPendingApprovals,
  SurveyApprovalAudiences,
  APIFieldAttributes,
  isEntity,
  QueryBuilderRule,
  QueryBuilderSubquery,
  UnpersistedAttributeQuestion,
  PersistedAttributeQuestion,
  PersistedDependentQuestion,
  HeaderField,
} from '@ardoq/api-types';
import { ConfirmResult } from '@ardoq/modal';
import { workspaceAccessControlInterface } from 'resourcePermissions/accessControlHelpers/workspace';
import { PermissionContext } from '@ardoq/access-control';
import { StepState } from '@ardoq/steppers';
import { WorkspaceModelDataByWorkspaceId } from 'streams/models/workspaceModelDataByWorkspaceId$';
import { toFieldTypeKey } from 'streams/fields/fields$';
import { pathTo } from 'router/declarative/pathTo';
import { isDateRangeFieldType } from '@ardoq/date-range';
import { isDateFieldType } from '@ardoq/date-time';
import { t, type I18nLocale } from '@ardoq/i18n';
import { broadcastApi } from '@ardoq/api';
import { isArdoqError } from '@ardoq/common-helpers';
import { logError } from '@ardoq/logging';
import { isEqual, omit } from 'lodash';
import {
  isConditionQuestion,
  isReferenceQuestion,
  isFieldQuestion,
  isHiddenFieldQuestion,
  isTagQuestion,
} from 'surveyAdmin/questions/questionPredicates';
import { getSubQuestions } from '@ardoq/surveys-ui';
import { APPROVER_FIELD_TYPES } from './consts';
import {
  AllPossibleQuestions,
  FieldsByComponentTypeName,
  MaybePersistedAttributeQuestion,
  MaybePersistedFieldQuestion,
  MaybePersistedReferenceQuestion,
  MaybePersistedSurvey,
  PendingAnswersActionForEditedSurvey,
  PendingAnswersInvalidationState,
  SurveyValidation,
} from 'surveyAdmin/types';
import {
  defaultRelationshipsAudience,
  defaultTraversalAudience,
  getRelationshipIdentifier,
  getRelationshipsAudience,
  getUsersAndGroupsAudience,
} from 'surveyAdmin/ChangeApproval/utils';
import { type Locale, localeCompare, localeCompareBase } from '@ardoq/locale';
import {
  confirmUnpublishDueToErrorModal,
  confirmUnpublishModal,
  confirmUnpublishDueToErrorWhenBroadcastsModal,
  confirmUnpublishWhenBroadcastsModal,
} from 'surveyAdmin/SurveyModals';

import {
  SearchFieldNames,
  composeSearchQuery,
} from 'search/AdvancedSearch/utils';
import { dispatchAction } from '@ardoq/rxbeach';
import { validationHelpers } from 'surveyAdmin/surveyValidation';
import {
  setHasInvalidatedSurveyChangeApproval,
  updateSurveyAttributes,
} from './streams/actions';
import { trackEvent } from 'tracking/tracking';
import { SurveyAdminMode } from 'surveyAdmin/navigation/types';
import { emptySurvey } from 'surveyAdmin/emptySurvey';

const { ROOT_WORKSPACE, TYPE_NAME } = SearchFieldNames;

export const checkIfSurveyConflictsWithChangeApprovalScope = (
  surveyAttributes: MaybePersistedSurvey
): boolean => {
  if (!hasFeature(Features.SURVEYS_CHANGE_APPROVAL_V2)) return false;

  return surveyAttributes.questions.some(
    question => SurveyQuestionType.TAG === question.type
  );
};

export const getSurveyWithoutSurveySectionStatuses = (
  surveyAttributes: MaybePersistedSurvey
) => {
  const copyOfMisc = {
    ...(surveyAttributes.misc ?? {}),
  };
  delete copyOfMisc.generalInformationSectionStatus;
  delete copyOfMisc.defineTheDataSectionStatus;
  delete copyOfMisc.questionsSectionStatus;
  delete copyOfMisc.feedbackMessageSectionStatus;
  delete copyOfMisc.landingPageSectionStatus;
  delete copyOfMisc.resultsDisplaySectionStatus;
  delete copyOfMisc.ardoqDiscoverSectionStatus;
  delete copyOfMisc.changeApprovalSectionStatus;
  delete copyOfMisc.permissionsAndVisibilitySectionStatus;

  return { ...surveyAttributes, misc: copyOfMisc };
};

const stripKeysFromSubQuestions = (
  questions: (UnpersistedReferenceSubQuestion | PersistedReferenceSubQuestion)[]
) => {
  return questions.map(question => omit(question, 'key'));
};

const stripKeysFromDependentQuestions = (
  questions: (UnpersistedFieldSubQuestion | PersistedFieldSubQuestion)[]
) => {
  return questions.map(question => omit(question, 'key'));
};

export const stripKeysFromQuestions = (
  questions: AllPossibleQuestions[]
): AllPossibleQuestions[] => {
  return questions.map(question => {
    if (isReferenceQuestion(question)) {
      return {
        ...omit(question, 'key'),
        properties: {
          ...question.properties,
          // Only include questions when defined to avoid converting undefined
          // to an empty array, which would cause doesSurveyHaveChanges to
          // detect a false diff against the persisted survey from the API.
          ...(question.properties.questions
            ? {
                questions: stripKeysFromSubQuestions(
                  question.properties.questions
                ),
              }
            : {}),
        },
      } as PersistedReferenceQuestion | UnpersistedReferenceQuestion;
    }
    if (isConditionQuestion(question)) {
      return {
        ...omit(question, 'key'),
        properties: {
          ...question.properties,
          ...(question.properties.questions
            ? {
                questions: stripKeysFromDependentQuestions(
                  question.properties.questions
                ),
              }
            : {}),
        },
      } as PersistedFieldQuestion | UnpersistedFieldQuestion;
    }

    return omit(question, 'key') as AllPossibleQuestions;
  });
};

type QuestionTypeCount = {
  attribute: number;
  field: number;
  reference: number;
  child: number;
  text: number;
  hiddenField: number;
  tag: number;
};

const determineQuestionTypes = (
  question: AllPossibleQuestions,
  questionTypeCount: QuestionTypeCount
) => {
  if (isConditionQuestion(question)) {
    const combinedQuestions = [
      question,
      ...(question.properties.questions ?? []),
    ];
    return combinedQuestions.reduce((updatedCount, question) => {
      return {
        ...updatedCount,
        [question.type]: updatedCount[question.type] + 1,
      };
    }, questionTypeCount);
  }
  return {
    ...questionTypeCount,
    [question.type]: questionTypeCount[question.type] + 1,
  };
};

const requiredQuestionsCountReducer = (
  currentRequiredQuestionsCount: number,
  question: AllPossibleQuestions
): number => {
  const requiredQuestionsCount = hasRequiredValidator(question) ? 1 : 0;

  if (!isConditionQuestion(question) && !isReferenceQuestion(question))
    return currentRequiredQuestionsCount + requiredQuestionsCount;

  const numberOfRequiredSubQuestions =
    question.properties.questions?.reduce(
      (acc, question) => acc + requiredQuestionsCountReducer(0, question),
      0
    ) ?? 0;
  return (
    currentRequiredQuestionsCount +
    requiredQuestionsCount +
    numberOfRequiredSubQuestions
  );
};

const getNumberOfQuestionsPerCategory = (questions: AllPossibleQuestions[]) => {
  return questions.reduce(
    (total, question) => {
      if (isConditionQuestion(question)) {
        const dependentCount = question.properties.questions?.length ?? 0;
        const questionTypeData = determineQuestionTypes(
          question,
          total.questionTypeCount
        );
        return {
          ...total,
          conditionalQuestions: total.conditionalQuestions + 1,
          dependentQuestions: total.dependentQuestions + dependentCount,
          totalQuestions: total.totalQuestions + (dependentCount + 1),
          questionTypeCount: questionTypeData,
          requiredQuestions: requiredQuestionsCountReducer(
            total.requiredQuestions,
            question
          ),
        };
      }

      return {
        ...total,
        questionTypeCount: determineQuestionTypes(
          question,
          total.questionTypeCount
        ),
        normalQuestions: total.normalQuestions + 1,
        totalQuestions: total.totalQuestions + 1,
        requiredQuestions: requiredQuestionsCountReducer(
          total.requiredQuestions,
          question
        ),
      };
    },
    {
      questionTypeCount: {
        attribute: 0,
        field: 0,
        reference: 0,
        child: 0,
        text: 0,
        hiddenField: 0,
        tag: 0,
      },
      conditionalQuestions: 0,
      dependentQuestions: 0,
      normalQuestions: 0,
      totalQuestions: 0,
      requiredQuestions: 0,
    }
  );
};

const getNumberOfUsersAndGroups = (selectors: SurveyApprovalAudiences) => {
  const groupLength = getUsersAndGroupsAudience(selectors).groupIds.length;
  const usersLength = getUsersAndGroupsAudience(selectors).userIds.length;
  return groupLength + usersLength;
};

const getNumberOfApproversByType = (approvalFlow: SurveyApprovalFlow) => {
  return {
    fallbackAudience: approvalFlow.fallbackAudience.audiencesIds.length,
    relationship: getRelationshipsAudience(approvalFlow.selectors ?? [])
      .relationships.length,
    usersAndGroups: getNumberOfUsersAndGroups(approvalFlow.selectors ?? []),
    // add this if possible
    traversal: 0,
  };
};

const getChangeApprovalTrackingData = (approvalFlow?: SurveyApprovalFlow) => {
  if (
    !approvalFlow ||
    !approvalFlow.enabled ||
    !hasFeature(Features.SURVEYS_CHANGE_APPROVAL_V2)
  )
    return { enabled: false };
  return {
    enabled: true,
    audienceType: approvalFlow.selectors?.map(
      selector => selector.audienceType
    ),
    numberOfApprovers: getNumberOfApproversByType(approvalFlow),
  };
};

export const getSurveyTrackingData = (
  surveyAttributes: MaybePersistedSurvey
) => {
  const { questions, approvalFlow } = surveyAttributes;
  const questionsAnalysis = getNumberOfQuestionsPerCategory(questions);
  const changeApprovalAnalysis = getChangeApprovalTrackingData(approvalFlow);
  return {
    questions: questionsAnalysis,
    changeApproval: changeApprovalAnalysis,
  };
};

const LEGACY_LAST_UPDATED_HEADER_FIELD_NAME = 'last-updated';
const LAST_UPDATED_HEADER_FIELD_NAME = 'lastUpdated';

const toCurrentHeaderFieldName = (name: string): string =>
  name === LEGACY_LAST_UPDATED_HEADER_FIELD_NAME
    ? LAST_UPDATED_HEADER_FIELD_NAME
    : name;

const normalizeCustomHeaderFields = (
  customHeaderFields: HeaderField[]
): HeaderField[] =>
  customHeaderFields.map(headerField => ({
    ...headerField,
    name: toCurrentHeaderFieldName(headerField.name),
  }));

export const cloneSurveyAttributes = (survey: APISurveyAttributes) => {
  const clonedSurvey = deepCopyObject(survey);
  clonedSurvey.misc = clonedSurvey.misc || {};
  clonedSurvey.filterSetup =
    clonedSurvey.filterSetup || ({} as SurveyFilterSetupAttributes);
  clonedSurvey.customHeaderFields = normalizeCustomHeaderFields(
    clonedSurvey.customHeaderFields
  );
  return clonedSurvey;
};

export const getRelatedSurveys = (
  surveys: APISurveyAttributes[],
  surveyAttributes: MaybePersistedSurvey
) => {
  const currentSurveyId = isPersistedSurvey(surveyAttributes)
    ? surveyAttributes._id
    : null;
  return (
    surveys?.filter(
      survey =>
        survey._id !== currentSurveyId &&
        survey.componentTypeName === surveyAttributes.componentTypeName &&
        survey.workspace === surveyAttributes.workspace
    ) ?? []
  );
};

export const getOtherPrioritySurvey = (
  surveys: APISurveyAttributes[],
  surveyAttributes: MaybePersistedSurvey
): APISurveyAttributes | undefined =>
  surveyAttributes.workspace && surveyAttributes.componentTypeName
    ? getRelatedSurveys(surveys, surveyAttributes).find(survey =>
        Boolean(survey.discoverPriority)
      )
    : undefined;

export const getEditSurveyPath = (surveyId: ArdoqId) =>
  pathTo('/survey-admin/edit/:surveyId', { surveyId });

export const getComponentTypeAndModelId = (
  workspaceModelDataByWorkspaceId: WorkspaceModelDataByWorkspaceId,
  workspaceId?: ArdoqId | null,
  componentTypeName?: string | null
) => {
  const workspaceModelData = workspaceId
    ? workspaceModelDataByWorkspaceId[workspaceId]
    : undefined;
  if (!workspaceModelData) return {};

  const modelType = workspaceModelData.componentTypes.find(
    ({ name }) => name === componentTypeName
  );
  return {
    selectedModelId: workspaceModelData.modelId,
    selectedTypeId: modelType ? modelType.id : undefined,
    modelIsRigid: workspaceModelData.modelIsRigid,
  };
};

export const getSurveyComponentFields = (
  surveyAttributes: MaybePersistedSurvey,
  workspaceModelDataByWorkspaceId: WorkspaceModelDataByWorkspaceId,
  fieldsByComponentType: Map<string, APIFieldAttributes[]>
): APIFieldAttributes[] => {
  const { selectedModelId, selectedTypeId } = getComponentTypeAndModelId(
    workspaceModelDataByWorkspaceId,
    surveyAttributes.workspace,
    surveyAttributes.componentTypeName
  );
  if (!selectedModelId || !selectedTypeId) {
    return [];
  }
  return (
    fieldsByComponentType.get(
      toFieldTypeKey(selectedModelId, selectedTypeId)
    ) ?? []
  );
};

export const getUnpublishConfirmationDialog = async (
  survey: MaybePersistedSurvey,
  hasError = false,
  pendingAnswersInvalidationState: PendingAnswersInvalidationState
): Promise<false | ConfirmResult> => {
  if (!isPersistedSurvey(survey)) return false;
  if (!hasFeature(Features.BROADCASTS)) {
    return hasError
      ? confirmUnpublishDueToErrorModal(pendingAnswersInvalidationState)
      : confirmUnpublishModal(pendingAnswersInvalidationState);
  }
  const result = await broadcastApi.fetchByContentId(survey._id);
  if (isArdoqError(result)) {
    // TODO[ARD-22935] There doesn't seem to be any error handling walking up this component tree
    logError(result);
    return false;
  }
  const { broadcastIds } = result;

  if (broadcastIds.length) {
    return hasError
      ? confirmUnpublishDueToErrorWhenBroadcastsModal(
          pendingAnswersInvalidationState
        )
      : confirmUnpublishWhenBroadcastsModal(pendingAnswersInvalidationState);
  }

  return hasError
    ? confirmUnpublishDueToErrorModal(pendingAnswersInvalidationState)
    : confirmUnpublishModal(pendingAnswersInvalidationState);
};

export const doesNonReferenceQuestionHaveDateField = (
  question: AllPossibleQuestions,
  componentFields: APIFieldAttributes[]
) => {
  if (isFieldQuestion(question) || isHiddenFieldQuestion(question)) {
    const field = componentFields.find(field => {
      // Date range fields are represented as "start_date,end_date" in questionProperties.fieldName
      if (question.properties.fieldName?.includes(',')) {
        const [start, end] = question.properties.fieldName.split(',');
        return field.name === start || field.name === end;
      }
      return field.name === question.properties.fieldName;
    });

    return isDateFieldType(field?.type) || isDateRangeFieldType(field?.type);
  }
  return false;
};

export const currentUserHasNoAdminAccessToWorkspace = (
  permissionsContext: PermissionContext,
  workspace?: ArdoqId | null
) => {
  if (!workspace) return true;
  return !workspaceAccessControlInterface.canAdminWorkspace(
    permissionsContext,
    workspace,
    null
  );
};

export const currentUserHasNoEditAccessToWorkspace = (
  permissionsContext: PermissionContext,
  workspace?: ArdoqId | null
) => {
  if (!workspace) return true;
  return !workspaceAccessControlInterface.canEditWorkspace(
    permissionsContext,
    workspace,
    null
  );
};

export const currentUserHasNoAccessToWorkspace = (
  permissionsContext: PermissionContext,
  workspace?: ArdoqId | null
) => {
  if (!workspace) return true;
  return !workspaceAccessControlInterface.canAccessWorkspace(
    permissionsContext,
    workspace
  );
};

export const hasRequiredValidator = (question: AllPossibleQuestions) => {
  return (
    !isTagQuestion(question) &&
    (question.validators ?? []).some(
      (val: SurveyQuestionValidator) => val.type === 'required'
    )
  );
};

const hasMinimumValidator = (question: AllPossibleQuestions) => {
  if (!question.validators) return false;
  return question.validators.some(
    (val: SurveyQuestionValidator) =>
      val.type === 'range' && val.min && val.min > 0
  );
};

export const isRequiredQuestion = (question: AllPossibleQuestions) =>
  hasRequiredValidator(question) || hasMinimumValidator(question);

export const getSectionStepState = (
  sectionStatus: SurveySectionStatus,
  hasError: boolean,
  isPersistedSurvey: boolean
): StepState => {
  if (isPersistedSurvey && hasError) {
    return StepState.ERROR;
  }
  switch (sectionStatus) {
    case 'initial':
      return StepState.ACTIVE;
    case 'entered':
      return hasError ? StepState.MODIFIED : StepState.DONE;
    case 'left':
      return hasError ? StepState.ERROR : StepState.DONE;
  }
};

const isOutgoingTriple =
  (surveyComponentTypeName: string) => (triple: MetaModelTriple) => {
    return triple.sourceComponentType === surveyComponentTypeName;
  };

const isIncomingTriple =
  (surveyComponentTypeName: string) => (triple: MetaModelTriple) => {
    return triple.targetComponentType === surveyComponentTypeName;
  };

const getTargetFields = (
  fieldsByComponentTypeName: FieldsByComponentTypeName,
  triple: MetaModelTriple
) => {
  return fieldsByComponentTypeName[triple.targetComponentType] ?? [];
};

const getSourceFields = (
  fieldsByComponentTypeName: FieldsByComponentTypeName,
  triple: MetaModelTriple
) => {
  return fieldsByComponentTypeName[triple.sourceComponentType] ?? [];
};

const targetHasApproverField =
  (fieldsByComponentTypeName: FieldsByComponentTypeName) =>
  (triple: MetaModelTriple) => {
    const targetFields = getTargetFields(fieldsByComponentTypeName, triple);
    return targetFields.some(isApproverField);
  };

const sourceHasApproverField =
  (fieldsByComponentTypeName: FieldsByComponentTypeName) =>
  (triple: MetaModelTriple) => {
    const sourceFields = getSourceFields(fieldsByComponentTypeName, triple);
    return sourceFields.some(isApproverField);
  };

export const isApproverField = (field: APIFieldAttributes) => {
  return APPROVER_FIELD_TYPES.has(field.type);
};

export const compareRelationships =
  (locale: Locale) =>
  (
    relationshipA: SurveyApprovalRelationship,
    relationshipB: SurveyApprovalRelationship
  ) => {
    const componentTypeA = relationshipA.componentTypeName;
    const componentTypeB = relationshipB.componentTypeName;
    if (componentTypeA === 'Person' && componentTypeB !== 'Person') return -1;
    if (componentTypeB === 'Person' && componentTypeA !== 'Person') return 1;
    return localeCompare(
      getRelationshipIdentifier(relationshipA),
      getRelationshipIdentifier(relationshipB),
      locale
    );
  };

export const getApproverFieldsByComponentTypeName = (
  fieldsByComponentTypeName: FieldsByComponentTypeName,
  componentTypeName: string
) => {
  return (fieldsByComponentTypeName[componentTypeName] ?? []).filter(
    isApproverField
  );
};

export const preselectContactEmailIfItExists = (
  fields: APIFieldAttributes[]
) =>
  fields.find(field => field.name === 'contact_email') ? 'contact_email' : null;

export const toSurveyApprovalRelationships = (
  metamodel: MetaModel,
  fieldsByComponentTypeName: FieldsByComponentTypeName,
  workspaceId: ArdoqId | null,
  componentTypeName: string
): SurveyApprovalRelationship[] => {
  if (!workspaceId) return [];
  const outgoingRelationships = metamodel.triples
    .filter(isOutgoingTriple(componentTypeName))
    .filter(targetHasApproverField(fieldsByComponentTypeName))
    .map(triple => {
      const targetFields = getTargetFields(fieldsByComponentTypeName, triple);
      return {
        outgoing: true,
        referenceTypeName: triple.referenceType,
        componentTypeName: triple.targetComponentType,
        emailField: preselectContactEmailIfItExists(targetFields),
      };
    });
  const incomingRelationships = metamodel.triples
    .filter(isIncomingTriple(componentTypeName))
    .filter(sourceHasApproverField(fieldsByComponentTypeName))
    .map(triple => {
      const sourceFields = getSourceFields(fieldsByComponentTypeName, triple);
      return {
        outgoing: false,
        referenceTypeName: triple.referenceType,
        componentTypeName: triple.sourceComponentType,
        emailField: preselectContactEmailIfItExists(sourceFields),
      };
    });

  return [...outgoingRelationships, ...incomingRelationships];
};

export const getFallbackUserAudience = (
  approvalFlow?: SurveyApprovalFlow
): SurveyApprovalFallbackAudience => {
  return (
    approvalFlow?.fallbackAudience ?? {
      audienceType: 'user',
      audiencesIds: [],
    }
  );
};

export const maybeResetApprovalFlowRelationshipSelector = (
  surveyAttributes: MaybePersistedSurvey,
  newAttributes: Partial<MaybePersistedSurvey>
): SurveyApprovalFlow | undefined => {
  const attributeKeys = Object.keys(newAttributes) as Array<
    keyof MaybePersistedSurvey
  >;
  const updatedSurvey = { ...surveyAttributes, ...newAttributes };
  const updatedApprovalFlow = updatedSurvey.approvalFlow;
  // we don't need to reset the audience selectors if:
  // - the change is not related to the workspace or component type
  // - change approvals are disabled
  // - the distributed feature is not enabled
  // - there is no relationship or traversal audience
  if (!isChangeToWorkspaceOrComponentType(attributeKeys)) {
    return updatedApprovalFlow;
  }
  if (!updatedApprovalFlow) return updatedApprovalFlow;
  if (!updatedApprovalFlow.enabled) return updatedApprovalFlow;
  if (!hasFeature(Features.SURVEYS_CHANGE_APPROVAL_V2)) {
    return updatedApprovalFlow;
  }
  const selectedAudienceType = updatedApprovalFlow.selectors?.[0]?.audienceType;
  if (!selectedAudienceType) return updatedApprovalFlow;
  if (
    selectedAudienceType !== 'relationships' &&
    selectedAudienceType !== 'traversal'
  ) {
    return updatedApprovalFlow;
  }
  return {
    ...updatedApprovalFlow,
    selectors:
      selectedAudienceType === 'relationships'
        ? [defaultRelationshipsAudience]
        : [defaultTraversalAudience],
  };
};

export const maybeResetNewComponentParentId = (
  surveyAttributes: MaybePersistedSurvey,
  newAttributes: Partial<MaybePersistedSurvey>,
  isWorkspaceModelRigid: boolean
) => {
  const attributeKeys = Object.keys(newAttributes) as Array<
    keyof MaybePersistedSurvey
  >;
  if (
    isChangeToWorkspaceOrComponentTypeWithRigidModel(
      attributeKeys,
      isWorkspaceModelRigid
    )
  ) {
    return null;
  }
  return attributeKeys.includes('newComponentParentId')
    ? newAttributes.newComponentParentId
    : surveyAttributes.newComponentParentId;
};

/**
 * A survey can only be priority for one workspace and component type, so
 * changing either of them resets the priority status.
 */
export const maybeResetDiscoverPriority = (
  surveyAttributes: MaybePersistedSurvey,
  newAttributes: Partial<MaybePersistedSurvey>
) => {
  const updatedSurvey = { ...surveyAttributes, ...newAttributes };
  if (!updatedSurvey.discoverPriority) {
    return updatedSurvey.discoverPriority;
  }
  if (updatedSurvey.workspace !== surveyAttributes.workspace) {
    return false;
  }
  if (updatedSurvey.componentTypeName !== surveyAttributes.componentTypeName) {
    return false;
  }
  return updatedSurvey.discoverPriority;
};

export type ExternallyManagedFieldNames = {
  componentFieldNames: ReadonlySet<string>;
  referenceFieldNames: ReadonlySet<string>;
};

export type ExternallyManagedFieldNamesByWorkspaceId = Readonly<
  Record<ArdoqId, ExternallyManagedFieldNames>
>;

const isComponentFieldExternallyManaged = (
  externallyManaged: ExternallyManagedFieldNamesByWorkspaceId,
  fieldName: string,
  workspaceId: ArdoqId
): boolean =>
  externallyManaged[workspaceId]?.componentFieldNames.has(fieldName) ?? false;

const isReferenceFieldExternallyManaged = (
  externallyManaged: ExternallyManagedFieldNamesByWorkspaceId,
  fieldName: string,
  workspaceId: ArdoqId
): boolean =>
  externallyManaged[workspaceId]?.referenceFieldNames.has(fieldName) ?? false;

const getReferenceQuestionWorkspaceId = (
  question: MaybePersistedReferenceQuestion,
  surveyWorkspaceId: ArdoqId
): ArdoqId | undefined =>
  question.properties.outgoing
    ? surveyWorkspaceId
    : question.properties.workspaceId;

export const getExternallyManagedWorkspaceIds = (
  questions: MaybePersistedSurvey['questions'],
  workspaceId: ArdoqId | null
): ArdoqId[] => {
  if (!workspaceId) return [];
  const workspaceIds = new Set<ArdoqId>([workspaceId]);
  questions.forEach(question => {
    if (question.type === 'reference') {
      const referenceWorkspaceId = getReferenceQuestionWorkspaceId(
        question,
        workspaceId
      );
      if (referenceWorkspaceId) {
        workspaceIds.add(referenceWorkspaceId);
      }
    }
  });
  return [...workspaceIds];
};

const makeConditionalFieldQuestionReadOnlyIfExternallyManaged = (
  subQuestion: PersistedDependentQuestion<PersistedFieldQuestion>,
  workspaceId: ArdoqId,
  externallyManaged: ExternallyManagedFieldNamesByWorkspaceId
) => {
  return isComponentFieldExternallyManaged(
    externallyManaged,
    subQuestion.properties.fieldName,
    workspaceId
  )
    ? { ...subQuestion, readonly: true }
    : subQuestion;
};

const makeConditionalAttributeQuestionReadOnlyIfExternallyManaged = (
  subQuestion: PersistedDependentQuestion<PersistedAttributeQuestion>,
  workspaceId: ArdoqId,
  externallyManaged: ExternallyManagedFieldNamesByWorkspaceId
) => {
  return isComponentFieldExternallyManaged(
    externallyManaged,
    subQuestion.properties.attributeName,
    workspaceId
  )
    ? { ...subQuestion, readonly: true }
    : subQuestion;
};

const makeConditionalQuestionReadOnlyIfExternallyManaged = (
  subQuestion: PersistedFieldSubQuestion,
  workspaceId: ArdoqId,
  externallyManaged: ExternallyManagedFieldNamesByWorkspaceId
): PersistedFieldSubQuestion => {
  if (subQuestion.type === 'field') {
    return makeConditionalFieldQuestionReadOnlyIfExternallyManaged(
      subQuestion,
      workspaceId,
      externallyManaged
    );
  }
  if (subQuestion.type === 'attribute') {
    return makeConditionalAttributeQuestionReadOnlyIfExternallyManaged(
      subQuestion,
      workspaceId,
      externallyManaged
    );
  }
  return subQuestion;
};

const makeFieldQuestionReadOnlyIfExternallyManaged = (
  question: MaybePersistedFieldQuestion,
  workspaceId: ArdoqId,
  externallyManaged: ExternallyManagedFieldNamesByWorkspaceId
): MaybePersistedFieldQuestion => {
  const readonly = isComponentFieldExternallyManaged(
    externallyManaged,
    question.properties.fieldName,
    workspaceId
  )
    ? true
    : question.readonly;

  if (isConditionQuestion(question)) {
    return {
      ...question,
      readonly,
      properties: {
        ...question.properties,
        questions: getSubQuestions(question).map(subQuestion => {
          return makeConditionalQuestionReadOnlyIfExternallyManaged(
            subQuestion,
            workspaceId,
            externallyManaged
          );
        }),
      },
    };
  }
  return {
    ...question,
    readonly,
  };
};

const makeAttributeQuestionReadOnlyIfExternallyManaged = (
  question: MaybePersistedAttributeQuestion,
  workspaceId: ArdoqId,
  externallyManaged: ExternallyManagedFieldNamesByWorkspaceId
): MaybePersistedAttributeQuestion => {
  return isComponentFieldExternallyManaged(
    externallyManaged,
    question.properties.attributeName,
    workspaceId
  )
    ? { ...question, readonly: true }
    : question;
};

const makeReferenceFieldQuestionReadOnlyIfExternallyManaged = (
  subQuestion: UnpersistedFieldQuestion | PersistedFieldQuestion,
  referenceWorkspaceId: ArdoqId,
  externallyManaged: ExternallyManagedFieldNamesByWorkspaceId
): UnpersistedFieldQuestion | PersistedFieldQuestion => {
  return isReferenceFieldExternallyManaged(
    externallyManaged,
    subQuestion.properties.fieldName,
    referenceWorkspaceId
  )
    ? { ...subQuestion, readonly: true }
    : subQuestion;
};

const makeReferenceAttributeQuestionReadOnlyIfExternallyManaged = (
  subQuestion: UnpersistedAttributeQuestion | PersistedAttributeQuestion,
  referenceWorkspaceId: ArdoqId,
  externallyManaged: ExternallyManagedFieldNamesByWorkspaceId
): UnpersistedAttributeQuestion | PersistedAttributeQuestion => {
  return isReferenceFieldExternallyManaged(
    externallyManaged,
    subQuestion.properties.attributeName,
    referenceWorkspaceId
  )
    ? { ...subQuestion, readonly: true }
    : subQuestion;
};

const makeReferenceSubQuestionReadOnlyIfExternallyManaged = (
  subQuestion: PersistedReferenceSubQuestion | UnpersistedReferenceSubQuestion,
  referenceWorkspaceId: ArdoqId,
  externallyManaged: ExternallyManagedFieldNamesByWorkspaceId
): PersistedReferenceSubQuestion | UnpersistedReferenceSubQuestion => {
  if (subQuestion.type === 'field') {
    return makeReferenceFieldQuestionReadOnlyIfExternallyManaged(
      subQuestion,
      referenceWorkspaceId,
      externallyManaged
    );
  }
  if (subQuestion.type === 'attribute') {
    return makeReferenceAttributeQuestionReadOnlyIfExternallyManaged(
      subQuestion,
      referenceWorkspaceId,
      externallyManaged
    );
  }
  return subQuestion;
};

const makeReferenceQuestionReadOnlyIfExternallyManaged = (
  question: MaybePersistedReferenceQuestion,
  workspaceId: ArdoqId,
  externallyManaged: ExternallyManagedFieldNamesByWorkspaceId
): MaybePersistedReferenceQuestion => {
  const subQuestions = question.properties.questions ?? [];
  const referenceWorkspaceId = getReferenceQuestionWorkspaceId(
    question,
    workspaceId
  );
  if (!referenceWorkspaceId) return question;
  return {
    ...question,
    properties: {
      ...question.properties,
      questions: subQuestions.map(subQuestion => {
        return makeReferenceSubQuestionReadOnlyIfExternallyManaged(
          subQuestion,
          referenceWorkspaceId,
          externallyManaged
        );
      }),
    },
  } as MaybePersistedReferenceQuestion;
};

const makeQuestionReadOnlyIfExternallyManaged = (
  question: MaybePersistedSurvey['questions'][0],
  workspaceId: ArdoqId,
  externallyManaged: ExternallyManagedFieldNamesByWorkspaceId
): MaybePersistedSurvey['questions'][0] => {
  switch (question.type) {
    case 'field':
      return makeFieldQuestionReadOnlyIfExternallyManaged(
        question,
        workspaceId,
        externallyManaged
      );
    case 'attribute':
      return makeAttributeQuestionReadOnlyIfExternallyManaged(
        question,
        workspaceId,
        externallyManaged
      );
    case 'reference':
      return makeReferenceQuestionReadOnlyIfExternallyManaged(
        question,
        workspaceId,
        externallyManaged
      );
    default:
      return question;
  }
};

export const makeQuestionsReadOnlyIfExternallyManaged = (
  questions: MaybePersistedSurvey['questions'],
  workspaceId: ArdoqId | null,
  externallyManaged: ExternallyManagedFieldNamesByWorkspaceId
): MaybePersistedSurvey['questions'] => {
  if (!workspaceId) return questions;
  // It might be tempting to return early here if the selected workspace is not
  // externally managed, but we still need to go through the questions to handle
  // sub questions of reference questions whose root workspace may be externally
  // managed.
  return questions.map(question =>
    makeQuestionReadOnlyIfExternallyManaged(
      question,
      workspaceId,
      externallyManaged
    )
  );
};

const isChangeToWorkspaceOrComponentType = (
  attributeKeys: Array<keyof MaybePersistedSurvey>
) =>
  attributeKeys.includes('componentTypeName') ||
  attributeKeys.includes('workspace');

const isChangeToWorkspaceOrComponentTypeWithRigidModel = (
  attributeKeys: Array<keyof MaybePersistedSurvey>,
  isWorkspaceModelRigid: boolean
) =>
  attributeKeys.includes('workspace') ||
  (attributeKeys.includes('componentTypeName') && isWorkspaceModelRigid);

export const isReferenceFilterEnabled = (
  filterSetup: SurveyFilterSetupAttributes | null
) => {
  return !!filterSetup?.enabled && filterSetup?.type === 'reference';
};

export const getHasPendingApprovals = (
  survey: MaybePersistedSurvey,
  surveysWithPendingApprovals: Partial<
    Record<ArdoqId, APISurveyPendingApprovals>
  >
) => {
  return Boolean(
    isPersistedSurvey(survey) &&
    survey.approvalFlow?.enabled &&
    (surveysWithPendingApprovals[survey._id]?.total ?? 0) > 0
  );
};

const hasEmptySubQuestionsKey = (properties: unknown): boolean => {
  if (typeof properties !== 'object' || properties === null) {
    return false;
  }
  if (!('questions' in properties)) {
    return false;
  }
  const { questions } = properties;
  return !Array.isArray(questions) || questions.length === 0;
};

/**
 * Returns a new array of questions, sorted by question _id (or key if no _id),
 * and with 'label' and 'description' fields removed from each question object.
 *
 * This is needed to perform comparisons agains questions on survey to determine whether
 * any pending answers will be deleted on save or not.
 *
 * Name, description, and order of questions does not cause invalidation of pending answers,
 * so we can safely remove these fields for the purpose of comparison.
 *
 * Similary, range validators are normalized to ensure they have 'min' and 'max' keys,
 * as undefined values for these may be omitted in API responses.
 *
 * A 'properties.questions' array that is absent, undefined or empty all mean the same
 * thing - no sub-questions - so the key is dropped in all three cases.
 */
export function normalizeQuestionsForEquality(
  questions: AllPossibleQuestions[],
  locale: Locale
) {
  // Sort by _id (persisted) or key (unpersisted)
  const sorted = [...questions].sort((a, b) => {
    const aId = (isEntity(a) ? a._id : a.key) || '';
    const bId = (isEntity(b) ? b._id : b.key) || '';
    return localeCompareBase(aId, bId, locale);
  });
  // Remove 'label' and 'description' from each question
  return sorted.map(q => {
    const cleaned = omit(q, ['label', 'description']) as AllPossibleQuestions;
    // Ensure range validators have min/max keys
    if (Array.isArray(cleaned.validators)) {
      cleaned.validators = cleaned.validators.map((v: any) => {
        if (v.type === 'range') {
          return {
            ...v,
            min: 'min' in v ? v.min : undefined,
            max: 'max' in v ? v.max : undefined,
          };
        }
        return v;
      });
    }
    if (hasEmptySubQuestionsKey(cleaned.properties)) {
      return { ...cleaned, properties: omit(cleaned.properties, 'questions') };
    }
    // Recursively clean subquestions if present
    if (
      cleaned.properties &&
      isReferenceQuestion(cleaned) &&
      Array.isArray(cleaned.properties.questions)
    ) {
      if (isEntity(cleaned)) {
        cleaned.properties = {
          ...cleaned.properties,
          questions: normalizeQuestionsForEquality(
            cleaned.properties.questions,
            locale
          ) as PersistedReferenceSubQuestion[],
        };
        return cleaned;
      }

      cleaned.properties = {
        ...cleaned.properties,
        questions: normalizeQuestionsForEquality(
          cleaned.properties.questions,
          locale
        ) as UnpersistedReferenceSubQuestion[],
      };
      return cleaned;
    }

    // The isConditionQuestion typeguard currently asserts to PersistedConditionFieldQuestion
    // (Not a union with UnpersistedConditionFieldQuestion).
    // The functionality below can be improved when this is fixed.
    if (
      cleaned.properties &&
      Array.isArray((cleaned.properties as any).questions)
    ) {
      (cleaned.properties as any) = {
        ...cleaned.properties,
        questions: normalizeQuestionsForEquality(
          (cleaned.properties as any).questions,
          locale
        ),
      };
      return cleaned;
    }
    return cleaned;
  });
}

export const questionsHavePendingAnswersInvalidatingChanges = (
  oldQuestions: AllPossibleQuestions[],
  newQuestions: AllPossibleQuestions[],
  locale: Locale
): boolean => {
  if (oldQuestions.length !== newQuestions.length) return true;

  const normalizedOldQuestions = normalizeQuestionsForEquality(
    oldQuestions,
    locale
  );
  const normalizedNewQuestions = normalizeQuestionsForEquality(
    newQuestions,
    locale
  );
  return !isEqual(normalizedOldQuestions, normalizedNewQuestions);
};

export const getSurveyEditorSaveButtonText = (
  isSaving: boolean,
  isChangesSaved: boolean,
  hasChanges: boolean,
  i18nLocale: I18nLocale
) => {
  if (isSaving) {
    return t(i18nLocale, 'surveyAdmin:editor.saving', 'Saving...');
  }
  if (isChangesSaved && !hasChanges) {
    return t(i18nLocale, 'surveyAdmin:editor.changesSaved', 'Changes saved');
  }
  return t(i18nLocale, 'surveyAdmin:editor.saveChanges', 'Save changes');
};

type GetSavableSurveyAttributes = {
  surveyAttributes: MaybePersistedSurvey;
  showAdvancedOptions: boolean;
  advancedSearchRules?: QueryBuilderSubquery | QueryBuilderRule;
  currentUserEmail?: string | null;
};
export const getSavableSurveyAttributes = ({
  surveyAttributes,
  showAdvancedOptions,
  advancedSearchRules,
  currentUserEmail,
}: GetSavableSurveyAttributes) => {
  const hasAdvancedSearchRules =
    showAdvancedOptions &&
    advancedSearchRules &&
    (advancedSearchRules as QueryBuilderSubquery).rules.length;

  const createdByOrContact =
    surveyAttributes.contactEmail ??
    (isPersistedSurvey(surveyAttributes)
      ? surveyAttributes.createdByEmail
      : undefined);

  const preppedSurveyAttributes = {
    ...surveyAttributes,
    contactEmail: createdByOrContact ?? currentUserEmail ?? undefined,
    componentSearch: composeSearchQuery({
      attributes: [
        {
          attribute: ROOT_WORKSPACE,
          value: surveyAttributes.workspace,
        },
        {
          attribute: TYPE_NAME,
          value: surveyAttributes.componentTypeName || null,
        },
      ],
      additionalConditions: hasAdvancedSearchRules
        ? (advancedSearchRules as QueryBuilderRule)
        : null,
    }),
    questions: stripKeysFromQuestions(surveyAttributes.questions),
  };

  return preppedSurveyAttributes;
};

type DoesSurveyHaveChanges = {
  survey: MaybePersistedSurvey;
  surveyAttributes: MaybePersistedSurvey;
  showAdvancedOptions: boolean;
  advancedSearchRules?: QueryBuilderSubquery | QueryBuilderRule;
};
export const doesSurveyHaveChanges = ({
  survey,
  surveyAttributes,
  showAdvancedOptions,
  advancedSearchRules,
}: DoesSurveyHaveChanges) => {
  const current = getSavableSurveyAttributes({
    surveyAttributes,
    showAdvancedOptions,
    advancedSearchRules,
  });
  const persistedSurvey = getSurveyWithoutSurveySectionStatuses(survey);

  const currentSurvey = getSurveyWithoutSurveySectionStatuses(current);

  return !isEqual(
    removeUndefinedFields(currentSurvey),
    removeUndefinedFields(persistedSurvey)
  );
};

const removeUndefinedFields = (survey: MaybePersistedSurvey) => {
  return JSON.parse(JSON.stringify(survey));
};

export const isAdvancedSearchQuery = (
  rule: QueryBuilderSubquery | QueryBuilderRule
): rule is QueryBuilderSubquery =>
  Object.prototype.hasOwnProperty.call(rule, 'condition');

export const promptAndTogglePublishSurvey = async ({
  pendingAnswersInvalidationState,
  surveyValidation,
  survey,
  surveyAttributes,
}: {
  pendingAnswersInvalidationState: PendingAnswersInvalidationState;
  surveyValidation: SurveyValidation | undefined;
  survey: MaybePersistedSurvey;
  surveyAttributes: MaybePersistedSurvey;
}) => {
  const hasError = validationHelpers.hasError(surveyValidation);

  if (!surveyAttributes.published && !hasError) {
    togglePublishSurvey({ survey, surveyAttributes });
    return;
  }

  return getUnpublishConfirmationDialog(
    survey,
    hasError,
    pendingAnswersInvalidationState
  ).then(confirmed => {
    if (!confirmed) return;
    if (
      pendingAnswersInvalidationState.action ===
      PendingAnswersActionForEditedSurvey.WILL_DELETE
    ) {
      dispatchAction(setHasInvalidatedSurveyChangeApproval(true));
    }
    return togglePublishSurvey({ survey, surveyAttributes });
  });
};

export const togglePublishSurvey = ({
  survey,
  surveyAttributes,
}: {
  survey: MaybePersistedSurvey;
  surveyAttributes: MaybePersistedSurvey;
}) => {
  const isSettingToLive = !surveyAttributes.published === true;

  if (isSettingToLive) {
    trackEvent('Survey live', getSurveyTrackingData(surveyAttributes));
  }
  if (survey) {
    dispatchAction(
      updateSurveyAttributes({
        published: !surveyAttributes.published,
      })
    );
  }
};

export const getSurvey = ({
  surveysById,
  surveyId,
  mode,
}: {
  surveysById: Record<ArdoqId, APISurveyAttributes>;
  surveyId: ArdoqId | null;
  mode: SurveyAdminMode;
}) => {
  const selectedSurvey = surveyId ? surveysById[surveyId] : null;
  const isCreatingNewSurvey = mode === SurveyAdminMode.NEW_SURVEY;

  return isCreatingNewSurvey || !selectedSurvey
    ? emptySurvey()
    : selectedSurvey;
};
